import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/AddOutlined"
import RepeatIcon from "@mui/icons-material/AutorenewOutlined"
import PlayIcon from "@mui/icons-material/PlayArrowOutlined"
import PauseIcon from "@mui/icons-material/PauseOutlined"
import BoltIcon from "@mui/icons-material/BoltOutlined"

import { billingApi } from "../../api"
import EmptyState from "../../components/EmptyState"
import { LoadingRows } from "../../components/LoadingState"
import { formatDate } from "../../utils/format"
import { rise } from "../../motion"
import ClientSelect from "../../components/ClientSelect"
import { money } from "./InvoiceTable"

/** Cadences somebody actually asks for, plus the escape hatch. */
const CADENCES = [
  { months: 1, label: "Monthly" },
  { months: 3, label: "Quarterly" },
  { months: 6, label: "Every six months" },
  { months: 12, label: "Annually" },
]

const STATUS_COLOR = { active: "success", paused: "warning", ended: "default" }

/**
 * Standing charges: the same amount, on the same day, until somebody stops it.
 *
 * ── The one thing this screen must never be vague about ─────────────────────
 *
 * Whether the next charge will be COLLECTED or merely raised. "Automatic" on a
 * client who has not authorised a card cannot take any money — it will produce
 * an invoice and fail, which is recoverable but only if somebody sees it coming.
 * So the server answers `will_collect_automatically` and `autopay_blocked`
 * rather than leaving this screen to infer it from two fields, and a blocked
 * schedule says so on its own row instead of in a tooltip.
 */
export default function RecurringPanel({ clients, onError, onNotice }) {
  const [schedules, setSchedules] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const [runsFor, setRunsFor] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await billingApi.schedules()
      setSchedules(data.schedules || [])
    } catch (err) {
      setSchedules([])
      onError?.(err.userMessage || "Could not load the recurring charges")
    }
  }, [onError])

  useEffect(() => {
    load()
  }, [load])

  const setStatus = async (schedule, status) => {
    try {
      await billingApi.updateSchedule(schedule.id, { status })
      onNotice?.(
        status === "paused"
          ? `${schedule.description} paused. Nothing further will be billed until it is resumed.`
          : `${schedule.description} resumed. Next charge ${formatDate(schedule.next_run_on)}.`,
      )
      load()
    } catch (err) {
      onError?.(err.userMessage || "Could not change the schedule")
    }
  }

  const runNow = async () => {
    setRunning(true)
    try {
      const result = await billingApi.runSchedules()
      const billed = result.results.filter((r) => r.status === "invoiced" || r.status === "charged")
      const failed = result.results.filter((r) => r.status === "failed")
      onNotice?.(
        result.considered === 0
          ? "Nothing is due today."
          : `${billed.length} invoice${billed.length === 1 ? "" : "s"} raised${failed.length ? `, ${failed.length} could not be collected` : ""}.`,
      )
      if (failed.length) onError?.(failed.map((f) => f.message).join(" · "))
      load()
    } catch (err) {
      onError?.(err.userMessage || "Could not run the due charges")
    } finally {
      setRunning(false)
    }
  }

  const blocked = useMemo(() => (schedules || []).filter((s) => s.autopay_blocked && s.status === "active"), [schedules])

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <RepeatIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h3">Recurring charges</Typography>
              <Typography variant="caption" color="text.secondary">
                Billed on their own, hourly. Run them now if you have just fixed the reason one failed.
              </Typography>
            </Box>
            <Tooltip title="Bill everything due today. Safe to press twice — a period can only be billed once.">
              <span>
                <Button size="small" startIcon={<BoltIcon />} onClick={runNow} disabled={running}>
                  {running ? "Running…" : "Run due now"}
                </Button>
              </span>
            </Tooltip>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New recurring charge
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {blocked.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {blocked.length === 1 ? "One charge is" : `${blocked.length} charges are`} set to collect automatically from a
          client who has not authorised a card. {blocked.length === 1 ? "It" : "They"} will raise an invoice on the due
          date and leave it for you to send.
        </Alert>
      ) : null}

      <Card>
        {schedules === null ? (
          <CardContent>
            <LoadingRows rows={4} />
          </CardContent>
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={RepeatIcon}
            title="No recurring charges"
            description="A retainer, a licence, a monthly fee — anything billed at the same amount on the same day."
            actionLabel="New recurring charge"
            onAction={() => setCreateOpen(true)}
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>What</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Every</TableCell>
                  <TableCell>Next</TableCell>
                  <TableCell>Collection</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule, index) => (
                  <TableRow key={schedule.id} hover sx={{ opacity: schedule.status === "active" ? 1 : 0.6, ...rise(index) }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {schedule.client_label}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" noWrap>
                        {schedule.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {money(schedule.amount_cents, schedule.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {schedule.cadence}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {schedule.status === "active" ? formatDate(schedule.next_run_on) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Three states, not two. "Automatic" that cannot collect
                          is the one worth knowing about. */}
                      {schedule.will_collect_automatically ? (
                        <Tooltip title={`Charged to ${schedule.card?.brand} ending ${schedule.card?.last4}`}>
                          <Chip size="small" color="success" label="Auto-charged" sx={{ height: 20, fontSize: 11 }} />
                        </Tooltip>
                      ) : schedule.autopay_blocked ? (
                        <Tooltip title="Set to collect automatically, but this client has not authorised a card. The invoice will be raised for you to send.">
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="No card"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip size="small" variant="outlined" label="Send to client" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" onClick={() => setRunsFor(schedule)}>
                          History
                        </Button>
                        {schedule.status === "active" ? (
                          <Tooltip title="Pause — nothing further is billed until it is resumed">
                            <Button size="small" color="inherit" startIcon={<PauseIcon />} onClick={() => setStatus(schedule, "paused")}>
                              Pause
                            </Button>
                          </Tooltip>
                        ) : schedule.status === "paused" ? (
                          <Button size="small" startIcon={<PlayIcon />} onClick={() => setStatus(schedule, "active")}>
                            Resume
                          </Button>
                        ) : (
                          <Chip size="small" label="Ended" sx={{ height: 20, fontSize: 11 }} color={STATUS_COLOR.ended} />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <ScheduleDialog
        open={createOpen}
        clients={clients}
        onClose={() => setCreateOpen(false)}
        onCreated={(schedule) => {
          setCreateOpen(false)
          onNotice?.(
            `${schedule.description} set up. First charge ${formatDate(schedule.next_run_on)}${
              schedule.will_collect_automatically ? ", taken from the card on file." : ", raised for you to send."
            }`,
          )
          load()
        }}
        onError={onError}
      />

      <RunHistoryDialog schedule={runsFor} onClose={() => setRunsFor(null)} onError={onError} />
    </>
  )
}

const EMPTY = {
  client_id: "",
  description: "",
  amount: "",
  every_months: 1,
  collection: "manual",
  starts_on: new Date().toISOString().slice(0, 10),
  ends_on: "",
}

/** Setting one up. The consequence of each choice is stated beside it, not after. */
function ScheduleDialog({ open, clients, onClose, onCreated, onError }) {
  const [form, setForm] = useState(EMPTY)
  const [autopay, setAutopay] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(EMPTY)
      setAutopay(null)
    }
  }, [open])

  // Whether the client chosen can actually be charged, asked as soon as they are
  // chosen rather than discovered on the first due date.
  useEffect(() => {
    if (!form.client_id) return setAutopay(null)
    let cancelled = false
    billingApi
      .autopay({ client_id: form.client_id })
      .then((data) => !cancelled && setAutopay(data))
      .catch(() => !cancelled && setAutopay(null))
    return () => {
      cancelled = true
    }
  }, [form.client_id])

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }))
  const amountCents = Math.round(Number(form.amount) * 100)
  const valid = form.client_id && form.description.trim() && Number.isFinite(amountCents) && amountCents > 0

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    try {
      const result = await billingApi.createSchedule({
        client_id: form.client_id,
        description: form.description.trim(),
        amount_cents: amountCents,
        every_months: Number(form.every_months),
        collection: form.collection,
        starts_on: form.starts_on,
        ends_on: form.ends_on || null,
      })
      onCreated(result.schedule)
    } catch (err) {
      onError?.(err.userMessage || "Could not create the recurring charge")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>New recurring charge</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <ClientSelect
              clients={clients}
              value={form.client_id}
              onChange={(id) => setForm((f) => ({ ...f, client_id: id }))}
              fullWidth
              required
            />

            <TextField
              label="What it is for"
              value={form.description}
              onChange={set("description")}
              fullWidth
              required
              helperText="This is the invoice line the client reads, so write it for them."
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Amount"
                value={form.amount}
                onChange={set("amount")}
                required
                fullWidth
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                helperText="The agreed figure. Tax is added from the client's settings."
              />
              <TextField select label="Every" value={form.every_months} onChange={set("every_months")} fullWidth>
                {CADENCES.map((c) => (
                  <MenuItem key={c.months} value={c.months}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="First charge" type="date" value={form.starts_on} onChange={set("starts_on")} fullWidth required />
              <TextField
                label="Stop after"
                type="date"
                value={form.ends_on}
                onChange={set("ends_on")}
                fullWidth
                helperText="Leave blank to run until stopped."
              />
            </Stack>

            <TextField select label="When it falls due" value={form.collection} onChange={set("collection")} fullWidth>
              <MenuItem value="manual">Raise an invoice for me to send</MenuItem>
              <MenuItem value="automatic">Charge the client's card</MenuItem>
            </TextField>

            {/* The one thing that must not be a surprise on the due date. */}
            {form.collection === "automatic" && form.client_id && autopay && !autopay.authorized ? (
              <Alert severity="warning">
                This client has not authorised a card. The charge will raise an invoice on the due date and leave it for
                you to send — it cannot collect anything until they authorise one from their Invoices screen.
              </Alert>
            ) : null}
            {form.collection === "automatic" && autopay?.authorized ? (
              <Alert severity="success">
                Will be taken from {autopay.card?.brand} ending {autopay.card?.last4}, with an invoice each time.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !valid}>
            {busy ? "Setting up…" : "Set up"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** Every period this charge has billed — the evidence behind each one. */
function RunHistoryDialog({ schedule, onClose, onError }) {
  const [runs, setRuns] = useState(null)

  useEffect(() => {
    if (!schedule) return setRuns(null)
    billingApi
      .scheduleRuns(schedule.id)
      .then((data) => setRuns(data.runs || []))
      .catch((err) => {
        setRuns([])
        onError?.(err.userMessage || "Could not load the history")
      })
  }, [schedule, onError])

  return (
    <Dialog open={Boolean(schedule)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {schedule?.description}
        <Typography variant="caption" color="text.secondary" display="block">
          {schedule?.client_label} · {schedule?.cadence}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {runs === null ? (
          <LoadingRows rows={3} height={40} />
        ) : runs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing billed yet. The first charge is {formatDate(schedule?.next_run_on)}.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run, index) => (
                <TableRow key={run.id} sx={rise(index)}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption">
                      {formatDate(run.period_start, "MMM D")} – {formatDate(run.period_end, "MMM D, YYYY")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {run.number || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">{run.total_cents != null ? money(run.total_cents) : "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={run.message || ""}>
                      <Chip
                        size="small"
                        label={run.status}
                        color={run.status === "charged" ? "success" : run.status === "failed" ? "error" : "default"}
                        variant={run.status === "invoiced" ? "outlined" : "filled"}
                        sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
